import React from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Modal from "aldoo-ra/Modal"
import Panel from "aldoo-ra/Panel"
import Tooltip from "aldoo-ra/Tooltip"
import Avatar from "aldoo-ra/Avatar"
import {
  KeyIcon,
  UserIcon,
  PlusIcon,
  MinusIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"

/**
 * Shared Permissions Editor Modal Component
 * Used by both UserPermissionsTab and CreateAPIKeyForm
 */
const PermissionsEditorModal = ({
  isOpen,
  onClose,
  user,
  availablePermissions,
  currentUserPermissions,
  permissionSearchTerm,
  setPermissionSearchTerm,
  currentPermissionSearchTerm,
  setCurrentPermissionSearchTerm,
  filteredAndGroupedPermissions,
  filteredCurrentUserPermissions,
  handleAddPermission,
  handleRemovePermission,
  handleAddAllResourcePermissions,
  handleRemoveAllResourcePermissions,
  handleUpdatePermissions,
  formError,
  loading
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Permissions"
      variant="admin_centered"
      className="max-w-6xl p-0"
      styleOverrides={{
        backdrop: "fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-70 !m-0"
      }}
      footerContent={
        <div className="flex justify-between items-center w-full">
          {/* User info in bottom left */}
          {user && (
            <div className="flex items-center gap-3">
              <Avatar 
                user={user} 
                variant="admin_modal" 
                textVariant="admin_label" 
                textSize="text-lg" 
              />

              <div className="space-y-0.5">
                <Typography 
                  variant="admin_label" 
                  className="leading-none my-0">
                  {user.name || 'Unknown User'}
                </Typography>

                <Typography variant="admin_txt2_dim">
                  {user.email || 'No email'}
                </Typography>
              </div>
            </div>
          )}
          
          {/* Action buttons in bottom right */}
          <div className="flex space-x-3">
            <Button
              variant="admin_secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
            
            <Button
              variant="admin_primary"
              onClick={handleUpdatePermissions}
              disabled={loading}
            >
              Update Permissions
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
          {/* Available Permissions */}
          <div className="flex flex-col min-h-0">
            <Typography 
              variant="admin_label" 
              className="flex items-center gap-2 flex-shrink-0 mb-3"
            >
              <KeyIcon className="w-4 h-4" />
              Available Permissions ({availablePermissions.length})
            </Typography>
            
            {/* Search Input */}
            <div className="flex-shrink-0 mb-5">
              <Input
                placeholder="Search permissions..."
                value={permissionSearchTerm}
                onChange={(e) => setPermissionSearchTerm(e.target.value)}
                variant="admin"
                className="w-full"
                leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                showClearButton={true}
                onClear={() => setPermissionSearchTerm("")}
              />
            </div>
            
            <div className="border border-admin_bordered rounded-xl overflow-hidden">
              <div className="p-4 h-full overflow-y-auto space-y-2">
              {Object.entries(filteredAndGroupedPermissions).length > 0 ? (
                Object.entries(filteredAndGroupedPermissions).map(([resource, groupData]) => (
                <Panel
                  key={`available-${resource}`}
                  variant="admin_bordered_shadow"
                  defaultOpen={false}
                  collapsible={true}
                  styleOverrides={{
                    header: "flex items-center p-4 bg-admin_primary/10 text-admin_text cursor-pointer",
                    panel: "border border-admin_bordered rounded-md overflow-hidden"
                  }}
                  
                  title={
                    <Tooltip content={groupData.resourceDescription} key={`tooltip-${resource}`}>
                      <Typography 
                        variant="admin_txt1" 
                        className="font-medium text-admin_text"
                      >
                        {resource}
                      </Typography>
                    </Tooltip>
                  }
                  headerContent={
                    <Button
                      variant="admin_secondary"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAddAllResourcePermissions(resource)
                      }}
                      className="text-xs bg-admin_paper h-6 px-2"
                      title={`Add all ${resource} permissions`}
                    >
                      <PlusIcon className="w-3 h-3 mr-1" />
                      Add All
                    </Button>
                  }
                >
                  <div className="flex flex-wrap gap-2">
                    {groupData.permissions.map((permission, index) => (
                      <Tooltip 
                        key={`available-perm-${permission.permission}-${index}`}
                        content={permission.description}
                      >
                        <div
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer ${
                            currentUserPermissions.includes(permission.permission)
                              ? 'bg-admin_primary/10 hover:bg-admin_error/10 text-admin_text border border-admin_primary/10 hover:border-admin_error/40'
                              : 'bg-admin_neutral_1/30 hover:bg-admin_primary/10 text-admin_text border border-admin_primary/40 hover:border-admin_primary/30'
                          }`}
                          onClick={() => handleAddPermission(permission)}
                        >
                          <Typography variant="admin_txt2" className="font-medium">
                            {permission.action}
                          </Typography>
                          
                          {!currentUserPermissions.includes(permission.permission) && (
                            <ArrowRightIcon className="w-3 h-3 text-admin_text_secondary" />
                          )}
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                </Panel>
              ))) : (
                <div className="text-center py-8">
                  <KeyIcon className="w-8 h-8 text-admin_text_secondary mx-auto mb-2" />
                  <Typography variant="admin_txt2_dim">
                    {permissionSearchTerm.trim() ? 'No permissions found matching your search' : 'No permissions available'}
                  </Typography>

                  {permissionSearchTerm.trim() && (
                    <Typography 
                      variant="admin_txt2_dim" 
                      className="block mt-1"
                    >
                      Try adjusting your search terms
                    </Typography>
                  )}
                </div>
              )}
              </div>
            </div>
          </div>

          {/* Current User Permissions */}
          <div className="flex flex-col min-h-0">
            <Typography 
              variant="admin_label" 
              className="flex items-center gap-2 flex-shrink-0 mb-3"
            >
              <UserIcon className="w-4 h-4" />
              Current User Permissions ({currentUserPermissions.length})
            </Typography>
            
            {/* Search Input */}
            <div className="flex-shrink-0 mb-5">
              <Input
                placeholder="Search current permissions..."
                value={currentPermissionSearchTerm}
                onChange={(e) => setCurrentPermissionSearchTerm(e.target.value)}
                variant="admin"
                className="w-full"
                leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                showClearButton={true}
                onClear={() => setCurrentPermissionSearchTerm("")}
              />
            </div>
            
            <div className="border border-admin_bordered rounded-xl overflow-hidden">
              <div className="h-full overflow-y-auto space-y-2 p-4">
              {Object.entries(filteredCurrentUserPermissions).length > 0 ? (
                Object.entries(filteredCurrentUserPermissions).map(([resource, groupData]) => (
                <Panel
                  key={`current-${resource}`}
                  variant="admin_bordered_shadow"
                  defaultOpen={false}
                  collapsible={true}
                  styleOverrides={{
                    header: "flex items-center p-4 bg-admin_neutral_1/40 text-admin_text cursor-pointer",
                    panel: "border border-admin_bordered rounded-md overflow-hidden"
                  }}
                  title={
                    <Tooltip content={groupData.resourceDescription} key={`current-tooltip-${resource}`}>
                      <Typography 
                        variant="admin_txt1" 
                        className="font-medium text-admin_text"
                      >
                        {resource}
                      </Typography>
                    </Tooltip>
                  }
                  headerContent={
                    <Button
                      variant="admin_secondary"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveAllResourcePermissions(resource)
                      }}
                      className="h-6 text-xs text-admin_error hover:text-white bg-admin_paper hover:bg-admin_error border-admin_error px-2"
                      title={`Remove all ${resource} permissions`}
                    >
                      <MinusIcon className="w-3 h-3 mr-1" />
                      Remove All
                    </Button>
                  }
                >
                  <div className="flex flex-wrap gap-2">
                    {groupData.permissions.map((permission, index) => (
                      <Tooltip 
                        key={`current-perm-${permission.permission}-${index}`}
                        content={permission.description}
                      >
                        <div
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer bg-admin_primary/10 hover:bg-admin_error/10 text-admin_text border border-admin_primary/10 hover:border-admin_error/40"
                          onClick={() => handleRemovePermission(permission.permission)}
                        >
                          <Typography variant="admin_txt2" className="font-medium">
                            {permission.action}
                          </Typography>
                          
                          <XMarkIcon className="w-3 h-3 text-admin_text_secondary" />
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                </Panel>
              ))) : (
                <div className="text-center py-8">
                  <KeyIcon className="w-8 h-8 text-admin_text_secondary mx-auto mb-2" />
                  <Typography variant="admin_txt2" className="text-admin_text_secondary">
                    {currentPermissionSearchTerm.trim() ? 'No permissions found matching your search' : 'No permissions assigned'}
                  </Typography>
                  <Typography variant="admin_txt2_dim" className="text-xs">
                    {currentPermissionSearchTerm.trim() ? 'Try adjusting your search terms' : 'Click on permissions from the left to add them'}
                  </Typography>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
        
        {formError && (
          <div className="bg-admin_error/10 border border-admin_error/20 rounded-lg p-3 flex items-start gap-2 flex-shrink-0 mt-4">
            <ExclamationTriangleIcon className="w-5 h-5 text-admin_error flex-shrink-0 mt-0.5" />

            <Typography 
              variant="admin_txt2" 
              className="text-admin_error"
            >
              {formError}
            </Typography>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default PermissionsEditorModal